import {useCallback, useEffect, useRef} from "react";
import {Input} from "@material-tailwind/react/components/Input";
import {useTranslation} from "react-i18next";
import FilterIcon from "../../assets/icons/FilterIcon";
import {Tooltip} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store";
import {setFiltersOpen} from "../../reducers/filters";

interface SearchWithFiltersProps {
    search: string;
    onSearchChanged: (e: React.ChangeEvent<HTMLInputElement>) => void;
    filters?: JSX.Element
}

export function SearchWithFilters({
                                      search,
                                      onSearchChanged,
                                      filters
                                  }: SearchWithFiltersProps) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const filtersOpen = useSelector((state: RootState) => state.filters.filtersOpen);
    // const [filtersOpen, setFiltersOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const isInsidePickerPortal = useCallback((target: EventTarget | null) => {
        if (!(target instanceof Element)) {
            return false;
        }

        const PICKER_CLASS_PREFIXES = [
            'datepicker-popover-content',
        ];

        let current: Element | null = target;
        while (current) {
            const hasPickerClass = Array.from(current.classList).some((className) =>
                PICKER_CLASS_PREFIXES.some((prefix) => className.startsWith(prefix))
            );
            if (hasPickerClass) {
                return true;
            }
            current = current.parentElement;
        }

        return false;
    }, []);

    useEffect(() => {
        dispatch(setFiltersOpen(false));
    }, [dispatch]);

    useEffect(() => {
        if (!filtersOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (!containerRef.current?.contains(event.target as Node) && !isInsidePickerPortal(event.target)) {
                dispatch(setFiltersOpen(false));
                // setFiltersOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [dispatch, filtersOpen, isInsidePickerPortal]);

    return (
        <div ref={containerRef} className="relative inline-flex items-stretch w-full md:max-w-[360px]">
            {filtersOpen && (
                <div
                    className="absolute z-50 mt-2 left-0 top-10 w-full max-w-[360px] bg-white dark:bg-black rounded-xl border border-blue-gray-50 shadow-lg">
                    <div className="p-3 border-b border-blue-gray-50 flex items-center justify-between">
                        <div className="font-semibold">{t('admin.datatable.filters')}</div>
                        <button
                            type="button"
                            onClick={() => dispatch(setFiltersOpen(false))}
                            className="text-blue-gray-200 hover:text-blue-gray-800"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="p-3">
                        {filters}
                    </div>
                </div>
            )}
            {/* SEARCH INPUT */}
            <Input
                crossOrigin=""
                variant="outlined"
                label={t("admin.datatable.search")}
                type="text"
                value={search}
                onChange={onSearchChanged}
                className={`
                    w-full
                    ${filters ? 'rounded-r-none border-r-0' : ''}
                    dark:text-gray-500
                    dark:border-blue-gray-800
                    dark:focus:border-gray-500
                    dark:placeholder-shown:border-blue-gray-800
                    `}
                labelProps={{
                    className:
                        "dark:peer-focus:text-white dark:peer-focus:before:!border-gray-500 dark:peer-focus:after:!border-gray-500",
                }}
            />

            {filters ?
                <Tooltip title={t('admin.datatable.filters')}>
                    <button
                        type="button"
                        onClick={() => dispatch(setFiltersOpen(!filtersOpen))}
                        className="
      flex items-center justify-center
      px-3
      bg-black text-white
      border border-l-0 border-blue-gray-800
      rounded-l-none rounded-r-md
      hover:bg-neutral-900
                            focus:outline-none focus:ring-2 focus:ring-white/30
    "
                        aria-label="Filters"
                    >
                        <FilterIcon/>
                    </button>
                </Tooltip>
                : ''}
        </div>
    );
}
